import React, { useEffect, useState } from "react";
import moment from "moment";
import ToggleSwitch from "@/common/custom/ToggleSwitch";
import { Shimmer } from "@/common/shimmer/Shimmer";
import Icon from "@/common/ui/Image";
import { H3, LightText, LightText2 } from "@/common/ui/Text";
import { Button } from "@/components/ui/button";
import { registeredMfa, sendPhoneOTP, toggledMfa, useSecurityStatus } from "@/services/common/security.services";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import ChangePassword from "./modal/ChangePassword";
import { toastMessage } from "@/utils/helper/myToast";
import VerifyPhoneNumber from "./modal/VerifyPhoneNumber";
import AuthenticatorSetup from "./modal/AuthenticatorSetup";
import Secure from "@/secure/payload";
import ChangePhone from "./modal/ChangePhone";
import { requestPasswordVerification, requestUserConfirmation } from "@/utils/helper/promise";
import VerifyItsYou from "@/components/common/modal/VerifyItsYou";
import { MfaType, MfaTypeKeys } from "@/utils/constant/security.constant";
import SmsMfaSetup from "./modal/SmsMfaSetup";
import AddPassword from "./modal/AddPassword";
import { Col, Row } from "@/common/css";
import ConfirmationAlertModal from "./modal/ConfirmationAlertModal";
import { DefaultErrorBoundary } from "@/common/ui/error/Error";
import VerifyItsYouAlert from "@/components/common/modal/VerifyItsYouAlert";
import { maskString } from "@/utils/helper/string";

const DisableMfaAlertModalContent = {
  descriptions: [
    "Disabling Two-Factor Authentication will remove an extra layer of security from your account, making it more vulnerable to unauthorized access.",
    "Are you sure you want to proceed?",
  ],
  submitText: "Disable anyway",
};

const DisableMfaVerifyItsYouModalContent = {
  submitText: "Confirm disable",
  descriptions: ["Please enter your password to disable Two-Factor Authentication. This action will reduce your account’s security."],
};

const modalConfig: ModalConfig = {
  setPassword: {
    component: ChangePassword,
    title: "Change password",
    modalClassName: "!max-w-[330px]",
  },
  userAlertConfirmation: {
    component: ConfirmationAlertModal,
    title: "Disable two factor authentication",
    modalClassName: "!max-w-[330px]",
  },
  addPassword: {
    component: AddPassword,
    title: "Set password",
    modalClassName: "!max-w-[330px]",
  },
  verifyPhone: {
    component: VerifyPhoneNumber,
    title: "Verify phone number",
    modalClassName: "!max-w-[330px]",
  },
  authenticatorSetUp: {
    component: AuthenticatorSetup,
    title: "Setup authenticator app in 3 steps",
    modalClassName: "!max-w-[486px]",
  },
  smsMfaSetUp: {
    component: SmsMfaSetup,
    title: "Two factor authentication",
    modalClassName: "!max-w-[330px]",
  },
  changePhone: {
    component: ChangePhone,
    title: "Change phone number",
    modalClassName: "!max-w-[330px]",
  },
  verifyItsYou: {
    component: VerifyItsYou,
    title: "Verifying it’s you",
    modalClassName: "!max-w-[330px]",
  },
  verifyItsYouAlert: {
    component: VerifyItsYouAlert,
    title: "Verifying it’s you",
    modalClassName: "!max-w-[330px]",
  },
};

type ButtonLoader = "verify" | "phone" | "" | MfaTypeKeys;

interface SecurityProps {
  openModal: (modalKey: string, props?: any) => void;
  closeModal: () => void;
  activeModal: string;
}

const Security: React.FC<SecurityProps> = ({ openModal, closeModal, activeModal }) => {
  const { data, isLoading, refetch }: any = useSecurityStatus();
  const [buttonLoader, setButtonLoader] = useState<ButtonLoader>("");
  const isAddPassword = data?.signupType === "GOOGLE" && !data?.isPasswordAdded;

  const handlePhoneVerify = async () => {
    try {
      setButtonLoader("verify");
      const res: any = await sendPhoneOTP();
      if (res?.status === 200) {
        const { message, isError, data, showMessage } = res.data;
        openModal("verifyPhone", { verifyData: Secure.payloadDecryption(data) });
        return showMessage && toastMessage(message, isError);
      } else {
        const { message, isError } = res?.response?.data || {};
        if (message) {
          return toastMessage(message, isError);
        } else {
          return toastMessage("Something went wrong", isError);
        }
      }
    } finally {
      setButtonLoader("");
    }
  };
  const handleChangePassword = (type: string) => {
    openModal(type);
  };

  const handleToggleMFA = async (mfaType: string, value: boolean) => {
    let isValidated = value;
    if (!isValidated) {
      try {
        await requestUserConfirmation({ openModal, closeModal, modalKey: "userAlertConfirmation", modalProps: DisableMfaAlertModalContent });
        await requestPasswordVerification({
          openModal,
          closeModal,
          modalKey: "verifyItsYouAlert",
          modalProps: DisableMfaVerifyItsYouModalContent,
          isSilent: true,
        });
        isValidated = true;
      } catch (err) {
        console.log("User Cancel---->", err);
      }
    }
    if (isValidated) {
      const res: any = await toggledMfa({ mfaType, enabled: value });
      if (res?.status === 200) {
        const { message, isError } = res.data;
        toastMessage(message, isError);
        refetch();
        return true;
      } else {
        console.log("Error", res);
        const { message, isError } = res?.response?.data || {};
        if (message) {
          toastMessage(message, isError);
          return false;
        }
        if (res.message) {
          toastMessage(res.message, true);
          return false;
        }
        return false;
      }
    }
    return isValidated;
  };

  const handleChangePhone = async () => {
    try {
      setButtonLoader("phone");
      try {
        await requestPasswordVerification({ openModal, closeModal });
      } catch (err) {
        console.log(err);
        return;
      }
      openModal("changePhone", { forceTitle: data?.phone ? undefined : "Add phone number" });
    } catch (error) {
      console.error("Failed to verify password:", error);
    } finally {
      setButtonLoader("");
    }
  };

  const handleMfaSetup = async (mfa: (typeof MfaType)[keyof typeof MfaType]) => {
    try {
      setButtonLoader(mfa.key as any);
      const res: any = await registeredMfa({ mfaType: mfa.key });

      if (res?.status === 201) {
        const { message, isError, showMessage, data } = res.data;
        if (showMessage) {
          toastMessage(message, isError);
        }
        openModal(mfa.setUpModalKey, { mfaDetails: Secure.payloadDecryption(data) });
      } else {
        console.log("Error", res);
        const { message, isError, showMessage } = res?.response?.data || {};
        if (message && showMessage) {
          return toastMessage(message, isError);
        }
        if (res.message) {
          toastMessage(res.message, true);
        }
      }
    } catch (err) {
      console.log({ err });
    } finally {
      setButtonLoader("");
    }
  };

  useEffect(() => {
    if (!activeModal) {
      refetch();
    }
  }, [activeModal]);

  return (
    <DefaultErrorBoundary>
      {/* Phone */}
      <Row className="">
        <Col className="w-full">
          <H3 text="Phone" />
          <Shimmer className="h-6 w-24" isLoading={isLoading}>
            {data?.phone && (
              <Row className=" gap-x-2">
                <LightText text={data?.phone} />
                {data?.isPhoneVerified ? (
                  <Icon alt="verified-icon" src="/icons/verified.svg" />
                ) : (
                  <Button variant="link" className="text-13 text-text-primary !p-1 " isLoading={buttonLoader === "verify"} onClick={handlePhoneVerify}>
                    Verify
                  </Button>
                )}
              </Row>
            )}
          </Shimmer>
        </Col>
        <Button variant="ghost" className="text-13 text-text-primary" isLoading={buttonLoader === "phone"} onClick={handleChangePhone}>
          {data?.phone ? "Change" : "Add"} phone
        </Button>
      </Row>
      {/* Password */}
      <Row className="">
        <Col className="w-full">
          <H3 text="Password" />
          <Row className=" gap-x-2">
            {data?.passwordUpdated && <LightText text={"Last updated " + moment(data.passwordUpdated).format("MMMM Do YYYY, h:mm A")} />}
          </Row>
        </Col>
        <Button variant="ghost" className="text-13 text-text-primary " onClick={() => handleChangePassword(isAddPassword ? "addPassword" : "setPassword")}>
          {isAddPassword ? "Add" : "Change"} password
        </Button>
      </Row>
      {/* 2FA */}
      <Col className="mt-6">
        <H3 text="Two factor authentication" />
        <LightText
          className="py-2  md:w-10/12 max-w-9k"
          text="Enabling Two-Factor Authentication adds an extra layer of security to your account. After activation, you'll need to provide both your password and an authentication code sent to your mobile phone to access your Lystproof account."
        />
        {/* SMS */}
        <Row className="">
          <Col className="w-full">
            <H3 text="Text message" />
            <Row className=" gap-x-2">
              <LightText text="Use your mobile phone to receive security code." />
            </Row>
            {data?.isSmsMfaAdded && (
              <Row className="gap-x-1.5">
                <Icon alt="verified-icon" src="/icons/verified.svg" />
                <LightText2 text="Verified" />
                {data?.phone && <LightText2 text={maskString(data.phone, { startDigits: 6 })} />}
              </Row>
            )}
          </Col>
          <Shimmer className="h-9 w-74" isLoading={isLoading}>
            {data?.isSmsMfaAdded ? (
              <ToggleSwitch initialState={data?.enabledMfaType === MfaType.SMS.key} showLabel onChange={handleToggleMFA} toggleId={MfaType.SMS.key} />
            ) : (
              <Button
                variant="outline"
                className="text-13 text-text-primary "
                isLoading={buttonLoader === MfaType.SMS.key}
                onClick={() => handleMfaSetup(MfaType.SMS)}
              >
                Setup
              </Button>
            )}
          </Shimmer>
        </Row>

        {/* Authenticator App */}
        <Row className="">
          <Col className="w-full">
            <H3 text="Authenticator app" />
            <Row className=" gap-x-2">
              <LightText text="Use your mobile phone to receive security code." />
            </Row>
          </Col>
          <Shimmer className=" h-9 w-74" isLoading={isLoading}>
            {data?.isAuthenticatorAdded ? (
              <ToggleSwitch
                initialState={data?.enabledMfaType === MfaType.AUTHENTICATOR.key}
                showLabel
                onChange={handleToggleMFA}
                toggleId={MfaType.AUTHENTICATOR.key}
              />
            ) : (
              <Button
                variant="outline"
                isLoading={buttonLoader === MfaType.AUTHENTICATOR.key}
                className="text-13 text-text-primary"
                onClick={() => handleMfaSetup(MfaType.AUTHENTICATOR)}
              >
                Setup
              </Button>
            )}
          </Shimmer>
        </Row>
      </Col>
    </DefaultErrorBoundary>
  );
};
export const SecurityWithModal = withModal(Security, modalConfig);
